import { Injector } from '@angular/core';
import { draggableNodeStopEvent } from '@examdojo/core/tiptap';
import { mergeAttributes, Node } from '@tiptap/core';
import { AngularNodeViewRenderer } from 'ngx-tiptap';
import { CustomComponentNodeComponent } from './custom-component.component';
import { CUSTOM_COMPONENT_NODE_NAME, DEFAULT_CUSTOM_COMPONENT_PROPS } from './custom-component.model';

const customComponentClassName = 'custom-component';

export const CustomComponentExtension = (injector: Injector) => {
  return Node.create({
    name: CUSTOM_COMPONENT_NODE_NAME,
    group: 'block',
    atom: true,
    selectable: true,

    addAttributes() {
      return {
        component_id: {
          default: DEFAULT_CUSTOM_COMPONENT_PROPS.component_id,
          renderHTML: (attributes) => {
            return { 'data-component-id': attributes['component_id'] };
          },
          parseHTML: (element) => {
            return element.getAttribute('data-component-id') ?? '';
          },
        },
        preset: {
          default: null,
          renderHTML: (attributes) => {
            return attributes['preset'] ? { 'data-preset': attributes['preset'] } : {};
          },
          parseHTML: (element) => {
            return element.getAttribute('data-preset') ?? null;
          },
        },
        inputs: {
          default: DEFAULT_CUSTOM_COMPONENT_PROPS.inputs,
          renderHTML: (attributes) => {
            const inputs = attributes['inputs'];
            if (!inputs) {
              return {};
            }
            return {
              'data-inputs': JSON.stringify(inputs),
            };
          },
          parseHTML: (element) => {
            const inputsStr = element.getAttribute('data-inputs');
            if (!inputsStr) {
              return DEFAULT_CUSTOM_COMPONENT_PROPS.inputs;
            }
            try {
              return JSON.parse(inputsStr);
            } catch {
              return DEFAULT_CUSTOM_COMPONENT_PROPS.inputs;
            }
          },
        },
        width: {
          default: null,
          renderHTML: (attributes) => {
            return attributes['width'] !== null && attributes['width'] !== undefined
              ? { 'data-width': attributes['width'] }
              : {};
          },
          parseHTML: (element) => {
            const val = element.getAttribute('data-width');
            return val ? Number(val) : null;
          },
        },
        height: {
          default: null,
          renderHTML: (attributes) => {
            return attributes['height'] !== null && attributes['height'] !== undefined
              ? { 'data-height': attributes['height'] }
              : {};
          },
          parseHTML: (element) => {
            const val = element.getAttribute('data-height');
            return val ? Number(val) : null;
          },
        },
        padding: {
          default: null,
          renderHTML: (attributes) => {
            return attributes['padding'] !== null && attributes['padding'] !== undefined
              ? { 'data-padding': attributes['padding'] }
              : {};
          },
          parseHTML: (element) => {
            const val = element.getAttribute('data-padding');
            return val ? Number(val) : null;
          },
        },
        align: {
          default: null,
          renderHTML: (attributes) => {
            return attributes['align'] ? { 'data-align': attributes['align'] } : {};
          },
          parseHTML: (element) => {
            return element.getAttribute('data-align') ?? null;
          },
        },
        show_border: {
          default: false,
          renderHTML: (attributes) => {
            return attributes['show_border'] === true ? { 'data-show-border': 'true' } : {};
          },
          parseHTML: (element) => {
            return element.getAttribute('data-show-border') === 'true' ? true : false;
          },
        },
        class: {
          renderHTML: (attributes) => {
            const otherClasses = ((attributes['class'] ?? '') as string)
              .split(' ')
              .filter((c) => c !== customComponentClassName);
            return { class: `${customComponentClassName} ${otherClasses.join(' ')}` };
          },
        },
      };
    },

    parseHTML() {
      return [
        {
          tag: `div.${customComponentClassName}`,
        },
      ];
    },

    renderHTML({ HTMLAttributes }) {
      return ['div', mergeAttributes({ class: customComponentClassName }, HTMLAttributes)];
    },

    addNodeView() {
      return AngularNodeViewRenderer(CustomComponentNodeComponent, {
        injector,
        stopEvent: draggableNodeStopEvent,
      });
    },
  });
};
